import { Link } from "react-router-dom";
import { FileText, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const Privacy = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <nav className="flex items-center justify-between p-6 md:p-8 border-b border-white/5">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-black tracking-tight">Trade Office</span>
        </Link>
        <Link to="/">
          <Button variant="ghost" className="gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </Link>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-muted-foreground mb-8">Effective Date: February 9, 2026</p>
        
        <div className="prose prose-invert max-w-none space-y-8">
          <section className="bg-card/50 border border-white/10 rounded-lg p-6">
            <h3 className="text-lg font-bold text-foreground mb-3">Data Controller</h3>
            <p className="text-muted-foreground mb-1"><strong className="text-foreground">Trade Office Solutions Limited</strong></p>
            <p className="text-muted-foreground mb-1">Company Number: 16896413</p>
            <p className="text-muted-foreground mb-1">Registered Office: 30 Grange Close, Leighton Buzzard, England, LU7 2PW</p>
            <p className="text-muted-foreground">
              Contact: <a href="mailto:toby@tradesoffice.co.uk" className="text-orange-500 hover:text-orange-400">toby@tradesoffice.co.uk</a>
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              Trade Office Solutions Limited ("we," "us," or "our") provides an AI-driven platform designed to streamline the quoting process for tradespeople. This policy details our commitment to protecting your personal data in accordance with the UK GDPR and the Data (Use and Access) Act 2025.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">2. Data Collection & Usage</h2>
            <p className="text-muted-foreground mb-4">We collect information necessary to facilitate your business workflow:</p>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">User Information:</strong> Name, email, and business details for account management.
              </li>
              <li>
                <strong className="text-foreground">Operational Data:</strong> Parts lists, labor rates, and project descriptions provided by you.
              </li>
              <li>
                <strong className="text-foreground">Automated Data:</strong> IP addresses and usage analytics to ensure system security and optimize our AI scraping performance.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">3. AI and Automated Decision-Making (ADM)</h2>
            <p className="text-muted-foreground mb-4">As an AI-first platform, we utilize automated processing to match your project requirements with third-party retailer pricing (e.g., Screwfix).</p>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Human-in-the-Loop:</strong> In line with the 2025 Act, our AI does not make "final" decisions. All quotes generated by our AI are <em>drafts</em> that require your meaningful review and manual approval before being sent to your customers.
              </li>
              <li>
                <strong className="text-foreground">Logic Disclosure:</strong> Our AI uses natural language processing (NLP) to interpret your parts lists and executes web-scraping protocols to retrieve the most recent public pricing.
              </li>
              <li>
                <strong className="text-foreground">Data Integrity:</strong> We do not share your customer lists or private business margins with the retailers we scrape.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">4. Lawful Basis for Processing</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Contractual:</strong> We process your data to fulfill our subscription agreement.
              </li>
              <li>
                <strong className="text-foreground">Legitimate Interests:</strong> Under the 2025 Act, we rely on legitimate interests for <em>Network Security</em> (detecting bot attacks) and <em>Direct Marketing</em> (keeping you updated on new features), subject to your right to opt-out.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">5. Data Storage & Location</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Storage Location:</strong> Your data is stored on secure cloud servers located within the United Kingdom and the European Economic Area (EEA). We use MongoDB Atlas for database storage, with primary servers hosted in UK data centres.
              </li>
              <li>
                <strong className="text-foreground">Third-Party Processors:</strong> We use the following service providers who may process your data:
                <ul className="mt-2 ml-4 space-y-1">
                  <li>• <strong>MongoDB Atlas</strong> (Database hosting - UK/EU servers)</li>
                  <li>• <strong>Stripe</strong> (Payment processing - PCI DSS compliant)</li>
                  <li>• <strong>OpenAI</strong> (AI quote generation - data processed per their DPA)</li>
                  <li>• <strong>Emergent</strong> (Authentication and hosting infrastructure)</li>
                </ul>
              </li>
              <li>
                <strong className="text-foreground">International Transfers:</strong> Where data is transferred outside the UK/EEA (e.g., to US-based AI providers), we ensure appropriate safeguards are in place, including Standard Contractual Clauses (SCCs) and adequacy decisions.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">6. Data Retention & Security</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Retention:</strong> We retain project data for the duration of your subscription. Financial records are held for 6 years as per HMRC requirements.
              </li>
              <li>
                <strong className="text-foreground">Security:</strong> We utilise industry-standard encryption and secure API gateways. Under the 2025 Act, we conduct "Reasonable and Proportionate" security audits of our AI infrastructure.
              </li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-foreground mb-4">7. Your Rights</h2>
            <p className="text-muted-foreground mb-4">Under the current UK framework, you have the right to access, rectify, or erase your data.</p>
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
              <p className="text-muted-foreground">
                <strong className="text-foreground">New Right to Complain:</strong> If you have a concern about how your data is handled, you must first lodge a complaint with us. We will acknowledge your request within <em>30 days</em> and provide a substantive response without undue delay.
              </p>
            </div>
          </section>

          <section className="bg-card/50 border border-white/10 rounded-lg p-6">
            <h2 className="text-xl font-bold text-foreground mb-4">Important Disclaimers</h2>
            <ul className="space-y-3 text-muted-foreground">
              <li>
                <strong className="text-foreground">Pricing Disclaimer:</strong> Trade Office Solutions Limited uses AI to retrieve third-party pricing. These figures are <em>estimates only</em>. We are not liable for discrepancies between our AI-generated quotes and final retailer checkout prices.
              </li>
              <li>
                <strong className="text-foreground">User Responsibility:</strong> The User is solely responsible for verifying the accuracy of all parts, quantities, and prices before submitting a quote to a client.
              </li>
              <li>
                <strong className="text-foreground">Service Availability:</strong> We do not guarantee 100% uptime of our scraping agents, as third-party websites may update their structures without notice.
              </li>
            </ul>
          </section>
        </div>

        {/* Back to top / home */}
        <div className="mt-12 pt-8 border-t border-white/10 flex justify-between items-center">
          <Link to="/terms" className="text-orange-500 hover:text-orange-400 font-medium">
            ← Terms & Conditions
          </Link>
          <Link to="/" className="text-orange-500 hover:text-orange-400 font-medium">
            Back to Home →
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 md:px-8 border-t border-white/5 mt-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded flex items-center justify-center">
              <FileText className="w-5 h-5 text-primary-foreground" />
            </div>
            <span className="font-bold">Trade Office</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-sm text-muted-foreground hover:text-orange-500 transition-colors">
              Terms & Conditions
            </Link>
            <Link to="/privacy" className="text-sm text-orange-500 font-medium">
              Privacy Policy
            </Link>
          </div>
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Trade Office Solutions Limited. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
